import React, { useId, useState } from 'react';

import AutoFixHighIcon from '@mui/icons-material/AutoFixHigh';
import { Button, MenuItem, Stack, TextField, Typography } from '@mui/material';

import { type AiCodeKind, generateAiCodeName } from '~/services/PromoVoucherAiService';

import PromoFeature from './PromoFeature';

export type AiWorkflowAction = 'create' | 'update' | 'create-or-update';

type Props = {
  kind: AiCodeKind;
  onContinue: (name: string, action: AiWorkflowAction, query: string) => void | Promise<void>;
  isPending?: boolean;
  compact?: boolean;
  initialAction?: AiWorkflowAction;
};

export default function PromoAiWorkflowLauncher({
  kind,
  onContinue,
  isPending = false,
  compact = false,
  initialAction = 'create-or-update',
}: Props) {
  const [name, setName] = useState('');
  const [query, setQuery] = useState('');
  const [action, setAction] = useState<AiWorkflowAction>(initialAction);
  const [isExpanded, setIsExpanded] = useState(false);
  const disabledReasonId = useId();
  const disabledReason = isPending
    ? 'Wait while the AI workflow finds the code.'
    : [
        !query.trim() && 'a query',
        action !== 'create' && !name.trim() && (kind === 'promo' ? 'a promo code name' : 'a voucher name'),
      ]
        .filter(Boolean)
        .join(' and ');
  const disabledMessage = isPending ? disabledReason : disabledReason ? `Enter ${disabledReason} to continue.` : '';
  const handleContinue = () => {
    const codeName = name.trim() || generateAiCodeName(kind);
    setName(codeName);
    void onContinue(codeName, action, query.trim());
  };

  return (
    <PromoFeature
      featureKey="ai-promo-tools-access"
      presentation={compact ? 'raw' : 'accordion'}
      accessMode="show-warning"
    >
      <Stack spacing={2}>
        <Stack direction={compact ? 'column' : { xs: 'column', sm: 'row' }} alignItems="flex-start" spacing={2}>
          <TextField
            label="Query"
            fullWidth
            multiline
            minRows={2}
            value={query}
            onFocus={() => setIsExpanded(true)}
            onChange={(event) => setQuery(event.target.value)}
            disabled={isPending}
            sx={{ flex: 1 }}
          />
          <TextField
            label={kind === 'promo' ? 'Promo code name' : 'Voucher name'}
            fullWidth
            value={name}
            required={action !== 'create'}
            helperText={action === 'create' ? 'Leave blank to generate a name automatically.' : undefined}
            onFocus={() => setIsExpanded(true)}
            onChange={(event) => setName(event.target.value)}
            disabled={isPending}
            sx={{ width: compact ? '100%' : { xs: '100%', sm: '30%' }, flexShrink: 0 }}
            onKeyDown={(event) => {
              if (event.key === 'Enter' && !disabledMessage) {
                event.preventDefault();
                handleContinue();
              }
            }}
          />
        </Stack>
        {isExpanded && (
          <Stack direction={compact ? 'column' : 'row'} spacing={2} sx={{ flexShrink: 0 }}>
            <TextField
              select
              label="Action"
              value={action}
              disabled={isPending}
              onChange={(event) => setAction(event.target.value as AiWorkflowAction)}
              sx={{ minWidth: 200 }}
            >
              <MenuItem value="create">Create</MenuItem>
              <MenuItem value="update">Update</MenuItem>
              <MenuItem value="create-or-update">Create or update</MenuItem>
            </TextField>
            <Stack spacing={1}>
              <Button
                variant="contained"
                startIcon={<AutoFixHighIcon />}
                disabled={!!disabledMessage}
                aria-describedby={disabledMessage ? disabledReasonId : undefined}
                onClick={handleContinue}
              >
                {isPending ? 'Finding...' : 'Continue with AI'}
              </Button>
              {disabledMessage && (
                <Typography id={disabledReasonId} variant="body2" color="text.secondary">
                  {disabledMessage}
                </Typography>
              )}
            </Stack>
          </Stack>
        )}
      </Stack>
    </PromoFeature>
  );
}
